import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowLeft,
  Check,
  X as XIcon,
  Sparkles,
  ShieldCheck,
  Plus,
  Clock,
} from "lucide-react";
import { CORSI, IMAGES, findCourse } from "../mock";
import Navbar from "./Navbar";
import Footer from "./Footer";
import WhatsAppFloat from "./WhatsAppFloat";
import Seo from "./Seo";

// Coherent course pairings — each course suggests a complementary one
const UPSELL_MAP = {
  "controlla-la-tua-comunicazione-digitale": {
    complement: "social-media-marketing",
    reason: "Dopo aver appreso il Metodo My Digital Action, il passo naturale è imparare a comunicarlo sui social — il canale dove i tuoi clienti passano ore ogni giorno.",
  },
  "social-media-marketing": {
    complement: "facebook-advanced",
    reason: "Padroneggiati i social organici, ora è il momento di amplificare i risultati con campagne Meta Ads professionali e scalabili.",
  },
  "facebook-advanced": {
    complement: "customer-loyalty",
    reason: "Se già investi in ads per acquisire clienti, il passo strategico è fidelizzarli e massimizzarne il valore nel tempo. LTV moltiplicato senza aumentare il budget.",
  },
  "customer-loyalty": {
    complement: "agenti-ai",
    reason: "Le automazioni di fidelizzazione diventano potentissime con gli Agenti AI. Aggiungi intelligenza artificiale per customer care e vendite 24/7.",
  },
  "agenti-ai": {
    complement: "controlla-la-tua-comunicazione-digitale",
    reason: "Gli Agenti AI danno il meglio con una strategia solida alla base. Il Metodo My Digital Action ti dà il framework strategico che l'AI eseguirà per te.",
  },
};

// Bundle discount: 2 courses at €399 instead of €260 + €260 = €520
const BUNDLE_PRICE = 399;

export default function UpsellPage({ onOpenBooking, onOpenCheckout }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  const mainCourse = findCourse(slug);
  const mapping = UPSELL_MAP[slug];
  const suggestedCourse = mapping
    ? CORSI.find((c) => c.slug === mapping.complement)
    : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [mainCourse]);

  const proceedSingle = () => {
    onOpenCheckout(mainCourse.slug, mainCourse.title, mainCourse.price);
  };

  const proceedBundle = () => {
    const bundleId = `bundle:${mainCourse.slug}`;
    const bundleName = `Bundle: ${mainCourse.title} + ${suggestedCourse.title}`;
    onOpenCheckout(bundleId, bundleName, BUNDLE_PRICE);
  };

  if (!mainCourse || !suggestedCourse) {
    return (
      <div className="App grain min-h-screen">
        <Navbar />
        <div className="container-max py-40 text-center">
          <div className="font-mono-label text-white/40 text-xs">404</div>
          <h1 className="font-display text-5xl text-white mt-4">Offerta non trovata</h1>
          <Link to="/" className="btn-primary mt-8 inline-flex">
            <ArrowLeft size={16} /> Torna alla home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const singleTotal = parseInt(mainCourse.price, 10);
  const suggestedPrice = parseInt(suggestedCourse.price, 10);
  const sumIndividual = singleTotal + suggestedPrice;
  const savings = sumIndividual - BUNDLE_PRICE;

  return (
    <div className="App grain">
      <Seo title="Completa il tuo percorso — MyDigitalAction" noindex />
      <Navbar onOpenBooking={onOpenBooking} />

      <section className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 overflow-hidden">
        {/* Ambient background */}
        <div className="absolute inset-0 gradient-glow pointer-events-none opacity-60" />
        <div className="absolute inset-0 pointer-events-none">
          <img
            src={IMAGES.heroBanner}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover grayscale"
            style={{ opacity: 0.12 }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/85 to-[#0a0a0a]" />
        </div>

        <div className="container-max relative z-10">
          <button
            onClick={() => navigate(`/corso/${slug}`)}
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8 transition-colors"
          >
            <ArrowLeft size={14} /> Torna al corso
          </button>

          {/* Top badge */}
          <div className="flex justify-center mb-8">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/40 backdrop-blur-md px-4 py-1.5 text-[11px] tracking-widest uppercase text-white/80">
              <Sparkles size={12} style={{ color: "#d4ff3d" }} />
              Offerta esclusiva — solo per te
            </span>
          </div>

          {/* Title */}
          <div className="text-center max-w-4xl mx-auto">
            <span className="section-label">Un attimo prima del checkout</span>
            <h1 className="font-display text-[32px] sm:text-[42px] lg:text-[56px] leading-[1.05] font-bold text-white mt-6">
              Prendi anche questo corso.<br />
              <span className="italic font-light text-white/60">
                Risparmia €{savings} e completa il tuo ecosistema.
              </span>
            </h1>
            <p className="text-white/70 text-base lg:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
              {mapping.reason}
            </p>
          </div>

          {/* Bundle visualizer */}
          <div className="mt-14 lg:mt-20 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6 items-stretch">
              {/* Main course */}
              <div className="bg-card border border-white/10 rounded-2xl p-6 lg:p-7 relative">
                <div className="font-mono-label text-[10px] text-white/40 mb-3">
                  Corso già selezionato
                </div>
                <div className="font-display text-white text-xl lg:text-2xl leading-tight">
                  {mainCourse.title}
                </div>
                <div className="text-white/60 text-sm mt-2">{mainCourse.subtitle}</div>
                <div className="mt-5 flex items-center gap-3 text-white/70 text-xs">
                  <Clock size={12} /> {mainCourse.hours}
                </div>
                <ul className="mt-5 space-y-2 text-white/70 text-xs">
                  {mainCourse.modules.slice(0, 4).map((m, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check size={12} className="text-lime mt-0.5 flex-shrink-0" style={{ color: "#d4ff3d" }} />
                      <span>{m}</span>
                    </li>
                  ))}
                  {mainCourse.modules.length > 4 && (
                    <li className="text-white/40 text-[10px] ml-4">
                      + altri {mainCourse.modules.length - 4} moduli
                    </li>
                  )}
                </ul>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="font-display text-2xl text-white">€{mainCourse.price}</div>
                </div>
              </div>

              {/* Plus symbol */}
              <div className="hidden lg:flex items-center justify-center">
                <div
                  className="w-14 h-14 rounded-full border border-white/15 flex items-center justify-center bg-[#0f0f0f]"
                >
                  <Plus size={20} className="text-white/70" />
                </div>
              </div>

              {/* Suggested course */}
              <div
                className="bg-card border-2 rounded-2xl p-6 lg:p-7 relative"
                style={{ borderColor: "#3b5bff" }}
              >
                <div
                  className="absolute -top-3 left-4 px-2 py-1 rounded text-[10px] font-mono-label"
                  style={{ background: "#3b5bff", color: "#fff" }}
                >
                  Consigliato per te
                </div>
                <div className="font-mono-label text-[10px] text-white/40 mb-3">
                  Aggiungi al bundle
                </div>
                <div className="font-display text-white text-xl lg:text-2xl leading-tight">
                  {suggestedCourse.title}
                </div>
                <div className="text-white/60 text-sm mt-2">{suggestedCourse.subtitle}</div>
                <div className="mt-5 flex items-center gap-3 text-white/70 text-xs">
                  <Clock size={12} /> {suggestedCourse.hours}
                </div>
                <ul className="mt-5 space-y-2 text-white/70 text-xs">
                  {suggestedCourse.modules.slice(0, 4).map((m, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check
                        size={12}
                        className="mt-0.5 flex-shrink-0"
                        style={{ color: "#6b8bff" }}
                      />
                      <span>{m}</span>
                    </li>
                  ))}
                  {suggestedCourse.modules.length > 4 && (
                    <li className="text-white/40 text-[10px] ml-4">
                      + altri {suggestedCourse.modules.length - 4} moduli
                    </li>
                  )}
                </ul>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-2xl text-white">€{suggestedCourse.price}</span>
                    <span className="text-white/40 text-xs line-through decoration-white/30">
                      €{suggestedCourse.originalPrice}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bundle price summary */}
            <div
              className="mt-8 lg:mt-10 rounded-3xl p-8 lg:p-10 relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #0f0f0f 0%, #131427 100%)",
                border: "1px solid rgba(59,91,255,0.35)",
              }}
            >
              <div
                className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-25"
                style={{ background: "#3b5bff" }}
              />
              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7">
                  <div className="font-mono-label text-[11px]" style={{ color: "#6b8bff" }}>
                    Bundle 2 corsi — Sconto esclusivo
                  </div>
                  <div className="font-display text-white text-2xl lg:text-3xl mt-2 leading-tight">
                    Entrambi i corsi + tutti i 5 Bonus per ciascuno
                  </div>
                  <div className="mt-4 space-y-2 text-white/70 text-sm">
                    <div className="flex items-center gap-3">
                      <Check size={14} style={{ color: "#d4ff3d" }} />
                      <span>Bonus completi inclusi per entrambi i corsi (valore €8.286+)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Check size={14} style={{ color: "#d4ff3d" }} />
                      <span>Coaching one‑to‑one anche per il secondo corso</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Check size={14} style={{ color: "#d4ff3d" }} />
                      <span>Accesso illimitato ai laboratori settimanali</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <ShieldCheck size={14} style={{ color: "#d4ff3d" }} />
                      <span>Garanzia Zero Rischi valida per entrambi</span>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-5 text-center lg:text-right">
                  <div className="text-white/50 text-sm mb-1">Anziché €{sumIndividual}</div>
                  <div className="flex items-baseline gap-2 justify-center lg:justify-end">
                    <span className="font-display text-white text-6xl lg:text-7xl font-medium leading-none">
                      €{BUNDLE_PRICE}
                    </span>
                  </div>
                  <div
                    className="inline-block mt-3 px-3 py-1 rounded-full text-xs"
                    style={{ background: "rgba(212,255,61,0.15)", color: "#d4ff3d" }}
                  >
                    Risparmi €{savings}
                  </div>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={proceedBundle}
                className="btn-primary justify-center w-full"
                style={{ padding: "18px 26px" }}
              >
                <span>Sì, aggiungi anche questo corso — €{BUNDLE_PRICE}</span>
                <ArrowUpRight size={18} />
              </button>
              <button
                type="button"
                onClick={proceedSingle}
                className="btn-secondary justify-center w-full"
                style={{ padding: "18px 26px" }}
              >
                <XIcon size={16} className="text-white/50" />
                <span>No grazie, procedo solo con {mainCourse.n}</span>
              </button>
            </div>

            <p className="text-center text-white/40 text-xs mt-5">
              Puoi cambiare idea in qualsiasi momento prima di completare il pagamento.
              Garanzia Zero Rischi su entrambi i corsi.
            </p>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat courseName={mainCourse?.title?.split("—")[0].split("–")[0].trim()} />
    </div>
  );
}
